/**
 * Password Validation Mixin
 * 
 * Provides common password validation methods for enforcing password policies
 * across different components (ChangePasswordDialog, UserRecordDialog, etc.)
 */

export default {
  methods: {
    // Password character set validation helpers
    hasUppercase(password) {
      return /[A-Z]/.test(password)
    },
    hasLowercase(password) {
      return /[a-z]/.test(password)
    },
    hasNumeric(password) {
      return /[0-9]/.test(password)
    },
    hasSpecialChars(password) {
      return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)
    },
    hasUnicodeChars(password) {
      // Check for Unicode characters beyond basic ASCII
      return /[^\u0000-\u007F]/.test(password)
    },
    
    // Enhanced password security validation methods
    isCommonPassword(password) {
      // List of common weak passwords and patterns
      const commonPasswords = [
        'password', 'password1', 'password123', 'password1234',
        '123456', '123456789', '12345678', '1234567890',
        'qwerty', 'qwerty123', 'abc123', 'admin', 'administrator',
        'user', 'test', 'guest', 'welcome', 'login',
        'passw0rd', 'p@ssword', 'p@ssw0rd', 'letmein',
        'monkey', 'dragon', 'master', 'shadow', 'superman'
      ];
      
      const lowerPassword = password.toLowerCase();
      
      // Check exact matches
      if (commonPasswords.includes(lowerPassword)) {
        return true;
      }
      
      // Check for common patterns
      const commonPatterns = [
        /^password\d+$/i,           // password + numbers
        /^\d{4,}$/,                 // only numbers (4+ digits)
        /^[a-z]+\d{1,4}$/i,        // word + 1-4 numbers
        /^[a-z]{3,}\d{4}$/i,       // word + 4 digits (like word2024)
        /^(qwerty|asdf|zxcv)\d*$/i, // keyboard patterns
        /^(.)\1{3,}$/,              // repeated characters
        /^(abc|123)\w*/i,           // starts with abc or 123
      ];
      
      return commonPatterns.some(pattern => pattern.test(password));
    },
    
    containsPersonalInfo(password, userInfo) {
      // Check if password contains username (if available)
      if (userInfo && userInfo.user_name) {
        const username = userInfo.user_name.toLowerCase();
        const lowerPassword = password.toLowerCase();
        
        // Check if password contains username or part of it
        if (username.length >= 3 && lowerPassword.includes(username)) {
          return true;
        }
        
        // Check reverse username
        const reverseUsername = username.split('').reverse().join('');
        if (lowerPassword.includes(reverseUsername)) {
          return true;
        }
      }
      
      // Check if password contains user_id (if available)
      if (userInfo && userInfo.user_id) {
        const userId = String(userInfo.user_id).toLowerCase();
        const lowerPassword = password.toLowerCase();
        
        // Check if password contains user_id
        if (userId.length >= 1 && lowerPassword.includes(userId)) {
          return true;
        }
      }
      
      // Check for common personal info patterns
      const personalPatterns = [
        /\b(admin|user|test|demo)\b/i,
        /\b(company|organization|corp)\b/i,
        /\b(carnot|web|system)\b/i, // specific to this application
      ];
      
      return personalPatterns.some(pattern => pattern.test(password));
    },
    
    hasDictionaryWords(password) {
      // Common English dictionary words that should be avoided
      const dictionaryWords = [
        'welcome', 'computer', 'internet', 'security', 'access',
        'system', 'network', 'server', 'database', 'application',
        'software', 'hardware', 'technology', 'information', 'digital',
        'online', 'website', 'portal', 'platform', 'service',
        'business', 'company', 'office', 'work', 'project',
        'manager', 'director', 'supervisor', 'employee', 'staff',
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december',
        'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
      ];
      
      const lowerPassword = password.toLowerCase();
      
      // Check for exact dictionary words
      if (dictionaryWords.some(word => lowerPassword.includes(word))) {
        return true;
      }
      
      // Check for dictionary words with simple substitutions
      const substitutions = {
        'a': ['@', '4'], 'e': ['3'], 'i': ['1', '!'], 'o': ['0'], 's': ['$', '5'],
        't': ['7'], 'g': ['9'], 'l': ['1'], 'b': ['6'], 'z': ['2']
      };
      
      for (const word of dictionaryWords) {
        let variations = [word];
        
        // Generate common substitution variations
        for (const [letter, subs] of Object.entries(substitutions)) {
          const newVariations = [];
          for (const variation of variations) {
            for (const sub of subs) {
              newVariations.push(variation.replace(new RegExp(letter, 'g'), sub));
            }
          }
          variations = variations.concat(newVariations);
        }
        
        // Check if password contains any variation
        if (variations.some(variation => lowerPassword.includes(variation))) {
          return true;
        }
      }
      
      return false;
    },
    
    hasSequentialChars(password) {
      // Check for sequential characters (123, abc, etc.)
      const sequences = [
        '123456789', 'abcdefghijklmnopqrstuvwxyz',
        'qwertyuiop', 'asdfghjkl', 'zxcvbnm',
        '!@#$%^&*()', '9876543210', 'zyxwvutsrqponmlkjihgfedcba'
      ];
      
      const lowerPassword = password.toLowerCase();
      
      for (const sequence of sequences) {
        for (let i = 0; i <= sequence.length - 4; i++) {
          const subSeq = sequence.substring(i, i + 4);
          if (lowerPassword.includes(subSeq)) {
            return true;
          }
        }
      }
      
      return false;
    },
    
    isPasswordSecure(password, userInfo, config) {
      // Comprehensive security check with configurable options
      if (config.passwordBlockCommonWords && this.isCommonPassword(password)) {
        return { valid: false, reason: 'common' };
      }
      
      if (config.passwordBlockPersonalInfo && this.containsPersonalInfo(password, userInfo)) {
        return { valid: false, reason: 'personal' };
      }
      
      if (config.passwordBlockCommonWords && this.hasDictionaryWords(password)) {
        return { valid: false, reason: 'dictionary' };
      }
      
      if (config.passwordBlockSequentialChars && this.hasSequentialChars(password)) {
        return { valid: false, reason: 'sequential' };
      }
      
      // Check for sufficient complexity beyond character sets
      if (!this.hasMinimumComplexity(password, config)) {
        return { valid: false, reason: 'complexity' };
      }
      
      return { valid: true };
    },
    
    hasMinimumComplexity(password, config) {
      // Enhanced complexity check
      if (password.length < config.minimumPasswordLength) {
        return false;
      }
      
      // Require at least 3 different character types for enhanced security
      const charTypes = this.getPasswordCharacterSetsCount(password);
      if (charTypes < Math.max(3, config.passwordRequiredCharacterSets - 1)) {
        return false;
      }
      
      // Additional complexity rules
      // No more than 2 consecutive identical characters
      if (/(.)\1{2,}/.test(password)) {
        return false;
      }
      
      // Must have at least 2 different characters
      const uniqueChars = new Set(password.toLowerCase()).size;
      if (uniqueChars < 2) {
        return false;
      }
      
      return true;
    },
    
    getPasswordCharacterSetsCount(password) {
      let count = 0;
      if (this.hasUppercase(password)) count++;
      if (this.hasLowercase(password)) count++;
      if (this.hasNumeric(password)) count++;
      if (this.hasSpecialChars(password)) count++;
      if (this.hasUnicodeChars(password)) count++;
      return count;
    },
    
    isPasswordCharacterSetsValid(password, requiredSets) {
      return this.getPasswordCharacterSetsCount(password) >= requiredSets;
    },
    
    getPasswordCharacterSetsMessage(requiredSets) {
      return this.$options.filters.translate('Password must contain at least ' + requiredSets + ' different character types (uppercase, lowercase, numbers, special characters, unicode)');
    },
    
    getPasswordSecurityMessage() {
      return this.$options.filters.translate('Password must be unique, complex, and avoid common words or personal information. Avoid easily guessed passwords like "Password1234" or "Las Vegas".');
    },
    
    getPasswordValidationMessage(password, confirmPassword, userInfo, config) {
      if (password === '') {
        return this.$options.filters.translate('Password cannot be empty');
      }
      if (typeof password !== 'string') {
        return this.$options.filters.translate('Password must be a valid string');
      }
      if (password.length < config.minimumPasswordLength) {
        return this.$options.filters.translate('Password must be at least ' + config.minimumPasswordLength + ' characters');
      }
      if (!this.isPasswordCharacterSetsValid(password, config.passwordRequiredCharacterSets)) {
        return this.getPasswordCharacterSetsMessage(config.passwordRequiredCharacterSets);
      }
      
      // Enhanced security validation messages
      const securityCheck = this.isPasswordSecure(password, userInfo, config);
      if (!securityCheck.valid) {
        switch (securityCheck.reason) {
          case 'common':
            return this.$options.filters.translate('Password is too common. Please choose a more unique password.');
          case 'personal':
            return this.$options.filters.translate('Password should not contain personal information or username.');
          case 'dictionary':
            return this.$options.filters.translate('Password should not contain common dictionary words.');
          case 'sequential':
            return this.$options.filters.translate('Password should not contain sequential characters (e.g., 1234, abcd).');
          case 'complexity':
            return this.$options.filters.translate('Password does not meet minimum complexity requirements.');
          default:
            return this.$options.filters.translate('Password does not meet security requirements.');
        }
      }
      
      if (confirmPassword !== undefined && password !== confirmPassword) {
        return this.$options.filters.translate('Password not matched');
      }
      return '';
    },
    
    isPasswordValid(password, confirmPassword, userInfo, config) {
      if (password === '') {
        return false;
      }
      // if password is null, undefined, or not a string, it will fail the length check and return false
      if (typeof password !== 'string' || password.length < config.minimumPasswordLength) {
        return false;
      }

      if (password.length < config.minimumPasswordLength) {
        return false;
      }
      if (!this.isPasswordCharacterSetsValid(password, config.passwordRequiredCharacterSets)) {
        return false;
      }
      // Enhanced security validation
      const securityCheck = this.isPasswordSecure(password, userInfo, config);
      if (!securityCheck.valid) {
        return false;
      }
      if (confirmPassword !== undefined && password !== confirmPassword) {
        return false;
      }
      return true;
    }
  }
}
